import { logger } from "../utils";

export class RoamingSettingsService {
  private settings: Office.RoamingSettings | null;

  constructor(readonly keyRegistry: string = "cadchat_settings") {
    this.settings = Office.context?.roamingSettings || null;
  }

  public isRoamingAvailable(): boolean {
    if (!this.settings) this.settings = Office.context?.roamingSettings ?? null;
    return !!this.settings;
  }

  public getTrackedKeys(): string[] {
    return this.settings?.get(this.keyRegistry) || [];
  }

  private trackKey(key: string): void {
    const keys = this.getTrackedKeys();
    if (!keys.includes(key)) {
      keys.push(key);
      this.settings?.set(this.keyRegistry, keys);
    }
  }

  private untrackKey(key: string): void {
    const keys = this.getTrackedKeys().filter((k) => k !== key);
    this.settings?.set(this.keyRegistry, keys);
  }

  async set<T = string>(key: string, value: T): Promise<void> {
    if (this.isRoamingAvailable()) {
      this.settings!.set(key, value);
      this.trackKey(key);
      return new Promise((resolve, reject) => {
        this.settings!.saveAsync((result) => {
          result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error);
        });
      });
    } else {
      localStorage.setItem(key, JSON.stringify(value));
      return Promise.resolve();
    }
  }

  async setItem<T = string>(key: string, value: T): Promise<void> {
    if (this.isRoamingAvailable()) {
      this.settings!.set(key, value);
      this.trackKey(key);
      return new Promise((resolve, reject) => {
        this.settings!.saveAsync((result) => {
          result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error);
        });
      });
    } else {
      localStorage.setItem(key, JSON.stringify(value));
      return Promise.resolve();
    }
  }

  get<T = string>(key: string): T | undefined {
    if (this.isRoamingAvailable()) {
      return this.settings!.get(key);
    } else {
      const item = localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : undefined;
    }
  }

  async getItem<T = string>(key: string): Promise<T | undefined> {
    return new Promise((resolve) => {
      if (this.isRoamingAvailable()) {
        resolve(this.settings!.get(key));
      } else {
        const item = localStorage.getItem(key);
        resolve(item ? (JSON.parse(item) as T) : undefined);
      }
    });
  }

  async remove(key: string): Promise<void> {
    if (this.isRoamingAvailable()) {
      this.settings!.remove(key);
      this.untrackKey(key);
      return new Promise((resolve, reject) => {
        this.settings!.saveAsync((result) => {
          result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error);
        });
      });
    } else {
      localStorage.removeItem(key);
      return Promise.resolve();
    }
  }

  async removeItem(key: string): Promise<void> {
    if (this.isRoamingAvailable()) {
      this.settings!.remove(key);
      this.untrackKey(key);
      return new Promise((resolve, reject) => {
        this.settings!.saveAsync((result) => {
          result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error);
        });
      });
    } else {
      localStorage.removeItem(key);
      return Promise.resolve();
    }
  }

  async clear(): Promise<void> {
    if (this.isRoamingAvailable()) {
      const keys = this.getTrackedKeys();
      for (const key of keys) {
        this.settings!.remove(key);
      }
      this.settings!.remove(this.keyRegistry); // remove the tracker itself
      return new Promise((resolve, reject) => {
        this.settings!.saveAsync((result) => {
          result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error);
        });
      });
    } else {
      localStorage.clear();
      return Promise.resolve();
    }
  }
}
